import {
  CONTEXT_TODAS_AS_PERSONAS,
  apiConversations,
} from '@modules/aawz-intelligence-v2/api/apiConversations'
import { apiFolders } from '@modules/aawz-intelligence-v2/api/apiFolders'

/**
 * Mutações de pasta que sempre andam junto com um recarregamento das duas
 * listas (pastas e conversas).
 *
 * Extraído porque `chat-sidebar.vue` precisa exatamente do mesmo par
 * "muta → recarrega os dois" que `conversations.vue` já faz.
 *
 * ★ O teto de carga NÃO mora aqui: é `CONVERSAS_LIMITE_CARGA`, exportado por
 * `apiConversations.js`, que é o dono do contrato do endpoint. `conversations.vue`
 * ainda declara o seu próprio `CONVERSATIONS_LIMIT = 30` — ela é o histórico do
 * popup (`chat-ia-pop-up.vue`), congelada por decisão de escopo (D-8 do plano),
 * e migrá-la para este helper é passo de uma run posterior. O que muda agora é
 * que o código novo deixou de ser a segunda fonte do número.
 */

// Pasta e conversa mudam juntas: mover uma conversa altera a contagem da pasta
// de origem E a da de destino. Recarregar só um dos dois deixa contador stale.
//
// ★ `context` é o eixo de persona que a superfície chamadora usou para LISTAR
// (`view-chat.vue:211`, `contextoListagem`). Sem repeti-lo aqui, o recarregar
// vai sem eixo nenhum e o backend aplica o próprio default — `persona='CRM'`
// (`resource_conversations.py:109`, `default=DEFAULT_PERSONA_ID`). Na home,
// que lista todas as personas, isso encolhe a coluna calada: medido no RDS
// dev, escritório 83, `CRM` devolve 182 conversas contra 299 de `ALL`. O
// `todos_agentes` que a lateral imersiva mandava não existe no backend
// (`grep -rn "todos_agentes" aawz-web-backend/src` → vazio, medido em
// 27/08/2026) e o `reqparse` descarta argumento não registrado em silêncio.
//
// `storeModule`: a lateral (`conversations.vue`) é reusada por outro produto sob
// OUTRO namespace — sem repassar, a lista recarregada seria gravada no slice do
// IA CRM. Ausente ⇒ default do `apiConversations.carregar` (byte-idêntico).
function recarregarListas({ context, storeModule }) {
  const todosAgentes = context === CONTEXT_TODAS_AS_PERSONAS
  // O contador das pastas é por persona (`apiFolders.getFolders`): sem ela o
  // backend conta o universo do CRM, mesmo com o Consultor ativo.
  apiFolders.carregar({
    forceUpdate: true,
    persona: todosAgentes ? undefined : context,
    todosAgentes,
  })
  // Sem `limit`: o default do método já é `CONVERSAS_LIMITE_CARGA`.
  apiConversations.carregar({ context, storeModule })
}

// A resposta volta para o chamador mostrar o snack — quem decide como avisar o
// usuário é o componente, não o helper.
function recarregarQuandoOk(r, opts) {
  if (r && r.status === 200) recarregarListas(opts)
  return r
}

/**
 * Move uma conversa para uma pasta (ou para fora de todas, com `folderId`
 * nulo).
 *
 * @param {Object} params `{ conversationId, folderId, context, storeModule }`.
 *   `context` é o eixo de persona da listagem da superfície e `storeModule` o
 *   namespace onde a lista recarregada é gravada — ver `recarregarListas`.
 * @returns {Promise<Object>} Resposta da API, ou `null` sem `conversationId`.
 */
export async function moverConversaParaPasta({
  conversationId,
  folderId,
  context,
  storeModule,
} = {}) {
  if (!conversationId) return null
  const r = await apiFolders.moveConversation({
    id: conversationId,
    id_folder: folderId || null,
  })
  return recarregarQuandoOk(r, { context, storeModule })
}

/**
 * Exclui uma pasta. As conversas dela não são apagadas — voltam para "Sem
 * pasta", e é por isso que a lista de conversas também recarrega.
 *
 * @param {Object} folder Registro da pasta.
 * @param {Object=} opts `{ context, storeModule }` — eixo de persona da
 *   listagem e namespace de escrita da superfície; ver `recarregarListas`.
 * @returns {Promise<Object>} Resposta da API, ou `null` sem id.
 */
export async function excluirPasta(folder, { context, storeModule } = {}) {
  if (!folder || !folder.id) return null
  const r = await apiFolders.deleteFolder({ id: folder.id })
  return recarregarQuandoOk(r, { context, storeModule })
}
